import { ethers } from "ethers";
import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";

import { message } from "@/components";
import { GATA_ADDRESS, USDT_ADDRESS } from "@/consts";
import {
  gataContractAtom,
  gataWalletContractAtom,
  quoterContractAtom,
  routerContractAtom,
  signerAtom,
} from "@/stores/wallet";
import {
  formatTokenAmount,
  generateDeadline,
  parseTokenAmount,
} from "@/utils/gata-wallet";

const poolFee = 100; // Fee tier: 100 (0.01%), 500 (0.05%), 2500 (0.25%), 10000 (1%)
const sqrtPriceLimitX96 = 0n; // Set to 0 for no price limit

export const useGataWallet = () => {
  const signer = useAtomValue(signerAtom);
  const gataContract = useAtomValue(gataContractAtom);
  const gataWalletContract = useAtomValue(gataWalletContractAtom);
  const routerContract = useAtomValue(routerContractAtom);
  const quoterContract = useAtomValue(quoterContractAtom);

  const [balance, setBalance] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  /**
   * Execute Pancake V3 Router exactInputSingle via GataWallet
   * @param amountIn Input GATA amount
   * @param amountOutMin Minimum output USDT amount
   * @param recipient USDT recipient address (external address)
   * @param fee Pool fee tier (default: 2500 for 0.25%)
   * @returns Transaction hash and execution result
   */
  const swapViaWallet = async (
    amountIn: number,
    amountOutMin: number,
    recipient: string,
  ) => {
    try {
      if (!gataContract || !routerContract) {
        throw new Error("Contracts not initialized");
      }

      console.log("[swapViaWallet]", {
        amountIn,
        amountOutMin,
        recipient,
      });

      const amountInUnits = ethers.parseUnits(amountIn.toString());
      const amountOutMinUnits = ethers.parseUnits(amountOutMin.toString());

      // Step 1: Construct V3 Router call parameters for exactInputSingle
      const routerInterface = new ethers.Interface([
        "function exactInputSingle(tuple(address tokenIn,address tokenOut,uint24 fee,address recipient,uint256 deadline,uint256 amountIn,uint256 amountOutMinimum,uint160 sqrtPriceLimitX96)) external returns (uint256)",
      ]);

      const deadline = generateDeadline();

      const internalBal = await getWalletBalance(signer?.address || "");
      console.log("GataWallet internal balance:", internalBal);

      const APP_ROLE = ethers.keccak256(ethers.toUtf8Bytes("APP_ROLE"));
      const has = await gataContract.hasRole(
        APP_ROLE,
        await routerContract.getAddress(),
      );
      console.log("router has APP_ROLE:", has);

      // V3 exactInputSingle parameters (as a tuple)
      const params = {
        tokenIn: GATA_ADDRESS,
        tokenOut: USDT_ADDRESS,
        fee: poolFee,
        recipient,
        deadline,
        amountIn: amountInUnits,
        amountOutMinimum: amountOutMinUnits,
        sqrtPriceLimitX96,
      };
      console.log("params:", params);

      // Encode V3 Router swap call
      const data = routerInterface.encodeFunctionData("exactInputSingle", [
        params,
      ]);

      // Step 2: Set maxSpend (maximum GATA amount allowed to spend)
      const maxSpend = amountInUnits; // Or add buffer: amountInUnits * 110n / 100n

      // Step 3: Call GataWallet.exec
      const tx = await gataContract.exec(
        await routerContract.getAddress(),
        data,
        maxSpend,
      );

      console.log("GataWallet exec tx hash:", tx.hash);

      const receipt = await tx.wait();
      console.log("GataWallet exec tx mined:", receipt.blockNumber);

      // Parse return value (V3 returns uint256 amountOut)
      if (receipt.status === 1) {
        // For V3, we need to check logs differently since the return value is encoded differently
        const decoded = routerInterface.decodeFunctionResult(
          "exactInputSingle",
          receipt.logs?.[0]?.data || "0x",
        );
        console.log("Swap output amount:", decoded[0]); // uint256 amountOut
      }

      return {
        hash: tx.hash,
        receipt,
        success: receipt.status === 1,
      };
    } catch (error) {
      handleWalletError(error);
      throw error;
    }
  };

  /**
   * Get user's GATA balance in GataWallet
   * @param userAddress User address
   * @returns GATA balance
   */
  const getWalletBalance = async (userAddress: string) => {
    try {
      if (!gataWalletContract) {
        throw new Error("GataWallet contract not initialized");
      }

      setLoading(true);
      const balance = await gataWalletContract.balanceOf(userAddress);
      const formattedBalance = ethers.formatUnits(balance);
      setBalance(Number(formattedBalance));
      return formattedBalance;
    } catch (error) {
      console.error("Failed to get wallet balance:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const quoterGataToken = async (amount: number) => {
    try {
      if (!quoterContract) return;
      const amountInUnits = parseTokenAmount(amount);

      const params = {
        tokenIn: USDT_ADDRESS,
        tokenOut: GATA_ADDRESS,
        amountIn: amountInUnits,
        fee: poolFee,
        sqrtPriceLimitX96,
      };

      console.log("quoterGataToken params:", { params });

      const fn = quoterContract.getFunction("quoteExactInputSingle");
      const res = await fn.staticCall(params);
      const { amountOut: amountOutUnits } = res || {};
      const amountOut = formatTokenAmount(amountOutUnits);
      // console.log('amountOut:', { amount, amountOut })
      return Number(amountOut);
    } catch (error) {
      console.error("[quoterGataToken]", error);
      // handleWalletError(error);

      throw error;
    }
  };

  /**
   * Handle GataWallet related errors
   * @param error Error object
   */
  useEffect(() => {
    if (signer?.address && gataWalletContract) {
      getWalletBalance(signer.address);
    }
  }, [signer?.address, gataWalletContract]);

  const handleWalletError = (error: any) => {
    if (typeof error === "object" && error !== null) {
      const errorMessage = error.message || "";
      console.error("GataWallet error:", errorMessage);

      if (errorMessage.includes("AppNotAllowed")) {
        message.error(
          "Target contract not authorized, please contact admin to configure APP_ROLE.",
        );
      } else if (errorMessage.includes("ExceedMaxSpend")) {
        message.error(
          "Actual spending exceeds maximum allowed amount, please increase max amount or reduce transaction amount.",
        );
      } else if (errorMessage.includes("InsufficientBalance")) {
        message.error(
          "Insufficient GATA balance in Wallet, please deposit or reduce transaction amount.",
        );
      } else if ("code" in error && error.code === "ACTION_REJECTED") {
        message.error("Transaction cancelled");
      } else {
        message.error(
          "Transaction failed, possibly due to price changes or low slippage, please try again later.",
        );
      }
    } else {
      message.error("Unknown error, please try again later");
    }
  };

  return {
    swapViaWallet,
    getWalletBalance,
    quoterGataToken,
    balance,
    loading,
  };
};
